import { TextureLoader, SRGBColorSpace, type Texture } from 'three';
import { initEngine, TimelineLedger, type PlacedTrack, type OverrideConfig } from '../engine/ledger';

export interface TrackLinks {
  appleMusic?: string;
  spotify?: string;
  youtube?: string;
}

/** Соцсети артиста (ссылки на профиль, не на трек) — ядро медиа-базы. */
export interface ArtistLinks {
  instagram?: string;
  soundcloud?: string;
  spotify?: string;
  youtube?: string;
  bandcamp?: string;
  tiktok?: string;
  facebook?: string;
  x?: string;
  telegram?: string;
  website?: string;
}

export interface Track {
  id: number;
  title: string;
  artist: string;
  album: string;
  durationMs: number;
  audioUrl: string;
  coverUrl: string | null;
  // промо-маркировка: прямые ссылки на площадки (ядро медиа-базы)
  links?: TrackLinks;
}

interface Bootstrap {
  v: number;
  program: {
    epochMs: number;
    seed: string;
    mode: 'seeded_shuffle' | 'ordered_queue';
    pool: number[];
    gapMs: number;
    overrides?: OverrideConfig[]; // правки эфира из админки
  };
  tracks: Track[];
  // соцсети по имени артиста (ключ = artist), общие для всех его треков
  artists?: Record<string, ArtistLinks>;
}

// Фикс-старт станции (общий для всех клиентов) — определяет live-слот эфира.
// Позже придёт из API (serverMs/epoch). UTC, чтобы все считали одинаково.
const STATION_EPOCH = Date.UTC(2026, 5, 16, 0, 0, 0);

/**
 * Каталог эфира. Лента бесконечна и детерминирована (Rust/WASM) — слоты
 * адресуются АБСОЛЮТНЫМ индексом от epoch станции. `slotAtTime(now)` даёт
 * live-слот (что в эфире сейчас); сцена показывает окно вокруг него.
 * Обложки грузятся прогрессивно, аудио — стримом.
 */
export class Catalog {
  readonly coverTex: (Texture | null)[];
  private coverCbs: Array<(trackId: number, tex: Texture) => void> = [];
  private readonly artistMap: Map<string, ArtistLinks>;

  constructor(
    readonly N: number, // размер видимого окна спирали (платформ)
    readonly tracks: Track[],
    readonly ledger: TimelineLedger,
    readonly epochMs: number,
    artists: Record<string, ArtistLinks> = {},
  ) {
    this.coverTex = tracks.map(() => null);
    this.artistMap = new Map(Object.entries(artists).map(([k, v]) => [k.trim().toLowerCase(), v]));
  }

  /** Соцсети артиста по имени (регистронезависимо). */
  artistLinks(artist: string): ArtistLinks | undefined {
    return this.artistMap.get((artist || '').trim().toLowerCase());
  }

  trackIdAtSlot(absSlot: number): number {
    return this.ledger.trackAtSlot(absSlot);
  }
  trackOfSlot(absSlot: number): Track {
    return this.tracks[this.trackIdAtSlot(absSlot)];
  }
  durSecAtSlot(absSlot: number): number {
    return this.trackOfSlot(absSlot).durationMs / 1000;
  }
  /** Live-слот эфира в момент `now` (epoch ms). */
  slotAtTime(now: number): PlacedTrack | null {
    return this.ledger.slotAtTime(now);
  }

  onCover(cb: (trackId: number, tex: Texture) => void): void {
    this.coverCbs.push(cb);
  }
  loadCovers(maxAniso = 4): void {
    const loader = new TextureLoader();
    for (const t of this.tracks) {
      if (!t.coverUrl) continue;
      loader.load(t.coverUrl, (tex) => {
        tex.colorSpace = SRGBColorSpace;
        tex.anisotropy = maxAniso;
        this.coverTex[t.id] = tex;
        for (const cb of this.coverCbs) cb(t.id, tex);
      });
    }
  }
}

/**
 * Источник истины — бэкенд (`/api/bootstrap`); статический `bootstrap.json` —
 * фолбэк (офлайн / бэкенд недоступен). DataProvider-шов: Mock → Http.
 */
async function fetchBootstrap(): Promise<Bootstrap> {
  try {
    const r = await fetch('/api/bootstrap', { cache: 'no-store' });
    if (r.ok) return (await r.json()) as Bootstrap;
  } catch {
    /* бэкенд недоступен — берём статику */
  }
  return (await fetch('/data/bootstrap.json').then((r) => r.json())) as Bootstrap;
}

export async function loadCatalog(N = 60): Promise<Catalog> {
  await initEngine();
  const boot = await fetchBootstrap();
  const tracks = boot.tracks;
  const ledger = new TimelineLedger(
    {
      epochMs: STATION_EPOCH,
      seed: boot.program.seed,
      mode: boot.program.mode,
      pool: boot.program.pool,
      gapMs: boot.program.gapMs,
      overrides: boot.program.overrides ?? [], // правки эфира (из админки); [] для статик-фолбэка
    },
    tracks.map((t) => t.durationMs),
  );
  return new Catalog(N, tracks, ledger, STATION_EPOCH, boot.artists || {});
}
